import { Injectable } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { RecommendationSection } from 'app/library/recommendation-section/models/recommendation-section.model';
import { NgxsActionHelper } from 'app/projects/core/src/lib/services/action.helper';
import { BehaviorSubject, Observable } from 'rxjs';
import { IRecommendationSectionListState } from '../interfaces/recommendation-section-list-state.interface';
import {
    FetchModuleSections,
    FetchModuleSectionsFailure,
    FetchModuleSectionsSuccess,
    FetchSectionFailure,
    FetchSectionSuccess,
    UnpublishSection,
    UnpublishSectionFailure,
    UnpublishSectionSuccess,
} from '../actions';
import { FetchSectionById, SetSectionsMandatory, SetSectionsMandatoryFailure, SetSectionsMandatorySuccess } from '../actions/index';
import { Section } from '../models/section.model';
import { SectionState } from '../models/section.state';

@Injectable()
export class SectionService {
    @Select(SectionState.getModuleEntities)
    moduleSection$: Observable<Section[]>;

    @Select(SectionState.getEntity)
    currentSection$: Observable<Section>;

    private _saveSectionChanges = new BehaviorSubject<boolean>(null);
    public saveSectionChangesSubject$ = this._saveSectionChanges.asObservable();

    private _discardSectionChanges = new BehaviorSubject<boolean>(null);
    public discardSectionChangesSubject$ = this._discardSectionChanges.asObservable();

    private _recommendationSectionUpdated = new BehaviorSubject<IRecommendationSectionListState | null>(null);
    public recommendationSectionUpdated$ = this._recommendationSectionUpdated.asObservable();

    constructor(private _store: Store, private _actionHelper: NgxsActionHelper) {}

    getModuleSections(): Section[] {
        return this._store.selectSnapshot(SectionState.getModuleEntities);
    }

    getSectionById(sectionId: string): Section {
        return SectionState.getEntity(this._store.selectSnapshot(SectionState), sectionId);
    }

    getCurrentSection(): Section {
        return this._store.selectSnapshot(SectionState.getEntity);
    }

    async fetchModuleSections(moduleId: string): Promise<boolean> {
        this._store.dispatch(new FetchModuleSections(moduleId));

        return await this._actionHelper.race(FetchModuleSectionsSuccess, FetchModuleSectionsFailure).toPromise();
    }

    async fetchSectionById(sectionId: string): Promise<boolean> {
        this._store.dispatch(new FetchSectionById(sectionId));

        return await this._actionHelper.race(FetchSectionSuccess, FetchSectionFailure).toPromise();
    }

    unpublishSection(sectionId: string): Promise<boolean> {
        this._store.dispatch(new UnpublishSection(sectionId));

        return this._actionHelper.race(UnpublishSectionSuccess, UnpublishSectionFailure).toPromise();
    }

    setSectionsMandatory(sections: Section[], isMandatory: boolean): Promise<boolean> {
        this._store.dispatch(new SetSectionsMandatory({ sections, isMandatory }));

        return this._actionHelper.race(SetSectionsMandatorySuccess, SetSectionsMandatoryFailure).toPromise();
    }

    saveSectionChanges(value: boolean): void {
        this._saveSectionChanges.next(value);
    }

    discardSectionChanges(value: boolean): void {
        this._discardSectionChanges.next(value);
    }

    notifyRecommendationSectionUpdated(recommendationSection: RecommendationSection, showWarningIcon: boolean): void {
        this._recommendationSectionUpdated.next({ recommendationSection, showWarningIcon });
    }
}
